import { CollapsibleSection } from "components/collapsibleSection";
import Input from "components/input";
import { Label } from "components/label";
import Select from "components/select";
import Textarea from "components/textarea";
import TimePicker from "components/timePicker";
import { DiscountType, ICreateCouponForm, PaymentMethodKey } from "interfaces";
import { isEmpty } from "lodash";
import moment from "moment";
import { Dispatch, SetStateAction, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { PAYMENT_METHODS } from "utills/constants";
interface AdditionalSetupProps {
  isShowPaymentMethod: boolean;
  isShowUsagePerAccount: boolean;
  isShowDailyActiveTime: boolean;
  setIsShowPaymentMethod: Dispatch<SetStateAction<boolean>>;
  setIsShowUsagePerAccount: Dispatch<SetStateAction<boolean>>;
  setIsShowDailyActiveTime: Dispatch<SetStateAction<boolean>>;
}
export const AdditionalSetup = ({
  isShowPaymentMethod,
  setIsShowPaymentMethod,
  isShowUsagePerAccount,
  setIsShowUsagePerAccount,
  isShowDailyActiveTime,
  setIsShowDailyActiveTime,
}: AdditionalSetupProps) => {
  const {
    control,
    register,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext<ICreateCouponForm>(); // retrieve all hook methods

  const [isShowCouponDisplay, setIsShowCouponDisplay] = useState(false);
  const discountTypeWatch = useWatch({
    control: control,
    name: "discount_type_display",
  });

  const shouldShowCouponDisplay = () => {
    const discountTypeForCouponDisplay = [
      DiscountType.FIX_PRICE,
      DiscountType.PERCENT,
    ];
    return discountTypeForCouponDisplay.includes(discountTypeWatch);
  };

  const shouldShowPaymentMethods = () => {
    const discountTypeForPaymentMethods = [
      DiscountType.FIX_PRICE,
      DiscountType.PERCENT,
      DiscountType.QUANTITY_DISCOUNT,
      DiscountType.CAMPAIGN_DISCOUNT,
    ];
    return discountTypeForPaymentMethods.includes(discountTypeWatch);
  };

  const shouldShowUsagePerAccount = () => {
    const discountTypeForUsagePerAccount = [
      DiscountType.FIX_PRICE,
      DiscountType.PERCENT,
    ];
    return discountTypeForUsagePerAccount.includes(discountTypeWatch);
  };

  const shouldShowDailyActiveTime = () => {
    const discountTypeForDailyActiveTime = [
      DiscountType.FIX_PRICE,
      DiscountType.PERCENT,
    ];
    return discountTypeForDailyActiveTime.includes(discountTypeWatch);
  };

  const shouldShowSpecialDiscount = () => {
    const discountTypeForSpecialDiscount = [DiscountType.GROUP_DISCOUNT];
    return discountTypeForSpecialDiscount.includes(discountTypeWatch);
  };

  const handleCouponDisplayToggle = (checked: boolean) => {
    setIsShowCouponDisplay(checked);
    setValue("coupons.displayed_on_actiup", false);
    setValue("coupons.displayed_on_whitelabel", false);
    setValue("coupons.short_desc_en", "");
    setValue("coupons.short_desc_vi", "");
  };

  const handlePaymentMethodToggle = (checked: boolean) => {
    setIsShowPaymentMethod(checked);
    if (checked) {
      setValue("apply_to_payment_methods", [PaymentMethodKey.ALL]);
    } else {
      setValue("apply_to_payment_methods", []);
    }
  };

  const handleToggleUsagePerAccount = (checked: boolean) => {
    setIsShowUsagePerAccount(checked);
    setValue("max_times_used_per_account", null);
  };

  const handleToggleDailyActiveTime = (checked: boolean) => {
    setIsShowDailyActiveTime(checked);
    if (checked) {
      setValue("apply_from_hour", moment().format("HH:mm"));
      setValue("apply_to_hour", "23:59");
      setValue("max_times_used_per_day", null);
    } else {
      setValue("apply_from_hour", null);
      setValue("apply_to_hour", null);
      setValue("max_times_used_per_day", null);
    }
  };

  const handleToggleSpecialDiscount = (checked: boolean) => {
    setValue("special_discount", checked);
    setValue("coupon_code", null);
    setValue("customer_code", null);
  };

  return (
    <CollapsibleSection label="Additional Set-up" defaultOpen={false}>
      <div className="box-container">
        <div className="grid gap-x-4 gap-y-4 grid-cols-8 text-sm">
          <div className="col-span-full">
            <Label required id="max_times_used" name="Max Time To Use" />
            <Input
              id="max_times_used"
              type="number"
              error={errors["max_times_used"]}
              placeholder="Max Time To Use"
              {...register("max_times_used", {
                valueAsNumber: true,
              })}
            />
            {errors["max_times_used"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors["max_times_used"]?.message}
                </span>
              </p>
            ) : null}
          </div>
          {shouldShowCouponDisplay() && (
            <>
              <div className="col-span-full">
                <Input
                  type="checkbox"
                  label="Coupon Display"
                  id="couponDisplay"
                  checked={isShowCouponDisplay}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleCouponDisplayToggle(e.target.checked)
                  }
                />
              </div>
              {isShowCouponDisplay && (
                <>
                  <div className="col-span-full flex items-center gap-x-4">
                    <Controller
                      control={control}
                      name="coupons.displayed_on_actiup"
                      render={({ field: { onChange, value = true, ref } }) => (
                        <Input
                          type="checkbox"
                          label="Display on ActiUp"
                          id="coupons.displayed_on_actiup"
                          checked={value}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onChange(e.target.checked)
                          }
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="coupons.displayed_on_whitelabel"
                      render={({ field: { onChange, value = true, ref } }) => (
                        <Input
                          type="checkbox"
                          label="Display on Whitelabel"
                          id="coupons.displayed_on_whitelabel"
                          checked={value}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onChange(e.target.checked)
                          }
                        />
                      )}
                    />
                  </div>
                  <div className="col-span-full">
                    <Label
                      id="coupons.short_desc_vi"
                      name="Coupon Terms of Use"
                    />
                    <div>
                      <Controller
                        control={control}
                        name="coupons.short_desc_vi"
                        render={({ field: { onChange, value = "", ref } }) => {
                          return (
                            <div className="relative">
                              <Textarea
                                id="coupons.short_desc_vi"
                                placeholder="Coupon Terms of Use (vi)"
                                className="pr-10"
                                error={errors["coupons"]?.short_desc_vi}
                                onChange={onChange}
                                value={value}
                              />
                              <img
                                src="/images/flags/circle-vi.png"
                                alt="Language Icon"
                                className="size-5 absolute top-3 right-3"
                              />
                            </div>
                          );
                        }}
                      />
                      {errors["coupons"]?.short_desc_vi ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {errors["coupons"]?.short_desc_vi?.message}
                          </span>
                        </p>
                      ) : null}
                    </div>
                    <div className="mt-4">
                      <Controller
                        control={control}
                        name="coupons.short_desc_en"
                        render={({ field: { onChange, value = "", ref } }) => {
                          return (
                            <div className="relative">
                              <Textarea
                                id="coupons.short_desc_en"
                                placeholder="Coupon Terms of Use (en)"
                                className="pr-10"
                                error={errors["coupons"]?.short_desc_en}
                                onChange={onChange}
                                value={value}
                              />
                              <img
                                src="/images/flags/circle-en.png"
                                alt="Language Icon"
                                className="size-5 absolute top-3 right-3"
                              />
                            </div>
                          );
                        }}
                      />
                      {errors["coupons"]?.short_desc_en ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {errors["coupons"]?.short_desc_en?.message}
                          </span>
                        </p>
                      ) : null}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
          {shouldShowPaymentMethods() && (
            <div className="col-span-full sm:col-span-4">
              <Input
                type="checkbox"
                label="Payment Methods"
                id="isShowPaymentMethod"
                checked={isShowPaymentMethod}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handlePaymentMethodToggle(e.target.checked)
                }
              />
              {isShowPaymentMethod && (
                <div className="mt-4">
                  <Controller
                    control={control}
                    name="apply_to_payment_methods"
                    defaultValue={[]}
                    render={({ field: { onChange, value = [], ref } }) => (
                      <Select
                        isMulti
                        key={``}
                        inputRef={ref}
                        options={
                          value.includes(PaymentMethodKey.ALL)
                            ? []
                            : PAYMENT_METHODS
                        }
                        id="payment_method.ignore_payment_methods"
                        placeholder="Payment Methods"
                        value={value.map((id: string) => {
                          return PAYMENT_METHODS.find(
                            (item) => item.value === id
                          );
                        })}
                        onChange={(
                          selected: { label: string; value: string }[]
                        ) =>
                          selected.find(
                            (item) => item.value === PaymentMethodKey.ALL
                          )
                            ? onChange([PaymentMethodKey.ALL])
                            : onChange(selected.map((item) => `${item.value}`))
                        }
                        error={errors["apply_to_payment_methods"]}
                      />
                    )}
                  />
                  {errors["apply_to_payment_methods"] ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {errors["apply_to_payment_methods"]?.message}
                      </span>
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          )}
          {shouldShowUsagePerAccount() && (
            <div className="col-span-full sm:col-span-4">
              <Input
                type="checkbox"
                label="Max Usage Per Account"
                id="isShowUsagePerAccount"
                checked={isShowUsagePerAccount}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleToggleUsagePerAccount(e.target.checked)
                }
              />
              {isShowUsagePerAccount && (
                <div className="mt-4">
                  <Input
                    id="max_times_used_per_account"
                    type="number"
                    error={errors["max_times_used_per_account"]}
                    placeholder="Max Usage Per Account"
                    {...register("max_times_used_per_account")}
                  />
                  {errors["max_times_used_per_account"] ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {errors["max_times_used_per_account"].message}
                      </span>
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          )}
          {shouldShowDailyActiveTime() && (
            <div className="col-span-full">
              <Input
                type="checkbox"
                label="Daily Active Time"
                id="isShowDailyActiveTime"
                checked={isShowDailyActiveTime}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleToggleDailyActiveTime(e.target.checked)
                }
              />
              {isShowDailyActiveTime && (
                <div className="w-full mt-4 flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Label
                      required
                      id="apply_from_hour"
                      name="Daily Start Time"
                    />
                    <Controller
                      control={control}
                      name="apply_from_hour"
                      render={({ field: { onChange, value = "" } }) => {
                        return (
                          <TimePicker
                            id="apply_from_hour"
                            placeholder="Daily Start Time"
                            className="bg-white"
                            error={errors["apply_from_hour"]}
                            value={value}
                            onChange={(date: any) =>
                              date === null
                                ? onChange("")
                                : onChange(moment(date).format("HH:mm"))
                            }
                          />
                        );
                      }}
                    />
                    {errors["apply_from_hour"] ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {errors["apply_from_hour"].message}
                        </span>
                      </p>
                    ) : null}
                  </div>
                  <div className="flex-1">
                    <Label required id="apply_to_hour" name="Daily End Time" />
                    <Controller
                      control={control}
                      name="apply_to_hour"
                      render={({ field: { onChange, value = "" } }) => {
                        return (
                          <TimePicker
                            id="apply_to_hour"
                            placeholder="Daily End Time"
                            className="bg-white"
                            error={errors["apply_to_hour"]}
                            value={value}
                            onChange={(date: any) =>
                              date === null
                                ? onChange("")
                                : onChange(moment(date).format("HH:mm"))
                            }
                          />
                        );
                      }}
                    />
                    {errors["apply_to_hour"] ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {errors["apply_to_hour"].message}
                        </span>
                      </p>
                    ) : null}
                  </div>
                  <div className="flex-1">
                    <Label
                      id="max_times_used_per_day"
                      name="Daily Usage Limit"
                    />
                    <Input
                      id="max_times_used_per_day"
                      type="number"
                      error={errors["max_times_used_per_day"]}
                      placeholder="Daily Usage Limit"
                      {...register("max_times_used_per_day")}
                    />
                    {errors["max_times_used_per_day"] ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {errors["max_times_used_per_day"].message}
                        </span>
                      </p>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          )}
          {shouldShowSpecialDiscount() && (
            <div className="col-span-full">
              <Controller
                control={control}
                name="special_discount"
                render={({ field: { onChange, value = false, ref } }) => (
                  <Input
                    type="checkbox"
                    label="Special Discount"
                    id="special_discount"
                    checked={value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      handleToggleSpecialDiscount(e.target.checked);
                    }}
                  />
                )}
              />
              {watch("special_discount") === true && (
                <div className="w-full mt-4 flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Label required id="coupon_code" name="Coupon Code" />
                    <Input
                      id="coupon_code"
                      type="text"
                      error={errors["coupon_code"]}
                      placeholder="Coupon Code"
                      {...register("coupon_code")}
                    />
                    {errors["coupon_code"] ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {errors["coupon_code"].message}
                        </span>
                      </p>
                    ) : null}
                  </div>
                  <div className="flex-1">
                    <Label required id="customer_code" name="Customer Code" />
                    <Input
                      id="customer_code"
                      type="text"
                      error={errors["customer_code"]}
                      placeholder="Customer Code"
                      {...register("customer_code")}
                    />
                    {errors["customer_code"] ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {errors["customer_code"].message}
                        </span>
                      </p>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </CollapsibleSection>
  );
};
